/**
 * klaviyo-events.js -- Klaviyo client-side commerce event tracking.
 *
 * Owned by Epic 10 ticket #58 (EP-DEP-07).
 *
 * Why this exists: the headless storefront tracked "Added to Cart" to Klaviyo
 * by routing a `provider: 'KLAVIYO'` payload through InstantJS
 * (`window.InstantJS.track('ITEM_ADDED_TO_CART', [klaviyoEvent, ...])`, see
 * headless `components/ProductCard/.../useSizeClick.tsx` and
 * `templates/ProductTemplate/components/Form/index.tsx`). InstantJS is not part
 * of the Shopify theme, so that delivery path was lost in the migration and no
 * ATC event ever reached Klaviyo. This module restores the event by pushing it
 * directly onto Klaviyo's onsite queue.
 *
 * Note: Klaviyo's native Shopify integration only tracks server-side events
 * (Placed Order, Started Checkout, Ordered Product, ...) via webhooks. There is
 * no Shopify webhook for "Added to Cart" -- it is inherently client-side and
 * must be instrumented in the theme. The onsite script + `_learnq` queue this
 * module pushes to are loaded by `snippets/klaviyo-embed.liquid`, which also
 * defer-loads this file (only when Klaviyo is enabled for the active market).
 *
 * Exposes `window.RTLKlaviyo.trackAddedToCart(addResponse)`, called from every
 * add-to-cart success path (PDP form, card quick-add, cart-drawer upsell, and
 * the cart-drawer global form interception). Accepts the raw `/cart/add.js`
 * response in any shape it returns (a single line-item object from a
 * form-encoded POST, a `{ items: [...] }` object from a JSON POST, or an
 * array) and fires one "Added to Cart" event per added line, enriched with
 * whole-cart context (total value + item names) read from `/cart.js`.
 *
 * No framework. Vanilla JS, no dependencies, defer-loaded.
 */
(function () {
  'use strict';

  // Klaviyo's onsite queue. `_learnq` is initialised synchronously by
  // klaviyo-embed.liquid and drains once klaviyo.js finishes loading, so it is
  // always safe to push to. Prefer the modern `klaviyo` object when present.
  function queue() {
    if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      return window.klaviyo;
    }
    if (window._learnq && typeof window._learnq.push === 'function') {
      return window._learnq;
    }
    return null;
  }

  // Normalise every /cart/add.js response shape into an array of line items.
  function toItems(response) {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.items)) return response.items;
    if (response.id || response.variant_id || response.product_id) {
      return [response];
    }
    return [];
  }

  // Shopify prices are integer cents; Klaviyo expects a numeric dollar value.
  function money(cents) {
    var n = Number(cents);
    if (isNaN(n)) return 0;
    return Math.round(n) / 100;
  }

  function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//.test(path)) return path;
    return window.location.origin + path;
  }

  function buildItemProps(item) {
    return {
      ProductID: item.product_id,
      SKU: item.sku || '',
      ProductName: item.product_title || item.title || '',
      VariantName: item.variant_title || '',
      Quantity: item.quantity || 1,
      ItemPrice: money(item.price),
      RowTotal: money(item.line_price || item.final_line_price || item.price),
      ProductURL: absoluteUrl(item.url),
      ImageURL: item.image ? absoluteUrl(item.image) : '',
      ProductCategories: item.product_type ? [item.product_type] : []
    };
  }

  function trackAddedToCart(addResponse) {
    var q = queue();
    if (!q) return; // Klaviyo not enabled / not loaded -- nothing to do.

    var added = toItems(addResponse);
    if (!added.length) return;

    // Enrich with whole-cart context. Klaviyo's canonical "Added to Cart"
    // schema carries the running cart total and the full item list. Non-
    // blocking: if /cart.js fails, fall back to item-only data so the event
    // still fires.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        var cartValue = cart ? money(cart.total_price) : null;
        var cartItems = cart && Array.isArray(cart.items) ? cart.items : [];
        var itemNames = cartItems.map(function (i) {
          return i.product_title || i.title || '';
        });
        var itemsDetailed = cartItems.map(buildItemProps);

        added.forEach(function (item) {
          var itemProps = buildItemProps(item);
          var props = {
            $value: cartValue != null ? cartValue : itemProps.RowTotal,
            AddedItemProductName: itemProps.ProductName,
            AddedItemProductID: itemProps.ProductID,
            AddedItemSKU: itemProps.SKU,
            AddedItemCategories: itemProps.ProductCategories,
            AddedItemImageURL: itemProps.ImageURL,
            AddedItemURL: itemProps.ProductURL,
            AddedItemPrice: itemProps.ItemPrice,
            AddedItemQuantity: itemProps.Quantity,
            ItemNames: itemNames.length ? itemNames : [itemProps.ProductName],
            CheckoutURL: absoluteUrl('/checkout'),
            Items: itemsDetailed.length ? itemsDetailed : [itemProps]
          };
          try {
            q.push(['track', 'Added to Cart', props]);
          } catch (err) {
            if (window.console && console.warn) {
              console.warn('[klaviyo-events] Added to Cart track failed', err);
            }
          }
        });
      });
  }

  window.RTLKlaviyo = window.RTLKlaviyo || {};
  window.RTLKlaviyo.trackAddedToCart = trackAddedToCart;
})();
